import React from 'react';
import { 
  getSecureRequestConfig, 
  getCsrfToken, 
  clearAuthStorage,
  setupAxiosInterceptors,
  secureFetch
} from './utils/secureAuth';
import { registerServiceWorker, unregisterServiceWorker } from './utils/musicCache';

export const AdminContext = React.createContext({ 
  isAdmin: false, 
  token: '', 
  login: async () => {}, 
  logout: () => {},
  getAuthToken: () => ''
});

const ADMIN_SESSION_HINT_KEY = 'tenryu_admin_session_hint';

function setAdminSessionHint(on) {
  try {
    if (on) localStorage.setItem(ADMIN_SESSION_HINT_KEY, '1');
    else localStorage.removeItem(ADMIN_SESSION_HINT_KEY);
  } catch (_) {}
}

export function AdminProvider({ children }) {
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [token, setToken] = React.useState('');
  // 使用 useRef 防止重复日志（React Strict Mode 会执行两次 useEffect）
  const swUnregisterLoggedRef = React.useRef(false);

  // 获取 CSRF Token（使用 secureAuth 中的方法）
  const fetchCsrfToken = React.useCallback(async () => {
    // getCsrfToken 会自动处理获取和存储
    return await getCsrfToken();
  }, []);

  // 验证会话状态
  const validateSession = React.useCallback(async () => {
    // 离线状态下无法验证会话，直接返回 false，不显示管理按钮
    if (!navigator.onLine) {
      setIsAdmin(false);
      setToken('');
      setAdminSessionHint(false);
      return false;
    }
    
    try {
      // 这里是“是否已登录”的探测：未登录返回 401 是正常情况，不应该触发 refresh/logout 的连锁动作。
      // 所以不要用 secureFetch（它会在 401 时尝试 refresh）。
      const config = await getSecureRequestConfig({
        method: 'GET',
        cache: 'no-store'
      });
      const response = await fetch('/api/auth/session', config);
      
      if (!response.ok) {
        // HTTP 错误（如 500），记录警告
        console.warn(`[Admin] Session validation returned ${response.status}`);
        setIsAdmin(false);
        setToken('');
        setAdminSessionHint(false);
        return false;
      }
      
      // 解析响应数据
      const data = await response.json().catch(() => ({ success: false }));
      
      if (data.success) {
        setIsAdmin(true);
        setAdminSessionHint(true);
        return true;
      } else {
        // 未登录是正常状态，静默处理
        setIsAdmin(false);
        setToken('');
        // 未登录：清掉 hint，避免后续每次启动都探测一次
        setAdminSessionHint(false);
        return false;
      }
    } catch (e) {
      // 网络错误或其他异常，记录但不作为严重错误（可能是离线状态）
      if (e.name !== 'TypeError' || !e.message.includes('fetch')) {
        console.warn('[Admin] Session validation error:', e.message || e);
      }
      setIsAdmin(false);
      setToken('');
      setAdminSessionHint(false);
      return false;
    }
  }, []);

  // 初始化时验证会话
  React.useEffect(() => {
    const init = async () => {
      // 离线状态下不尝试获取 CSRF token 或验证会话
      if (!navigator.onLine) {
        setIsAdmin(false);
        setToken('');
        // 通知 header.js 当前登录状态（离线时不是 admin）
        try {
          window.dispatchEvent(new CustomEvent('app:admin-status-changed', { 
            detail: { isAdmin: false } 
          }));
        } catch (_) {}
        return;
      }
      
      try {
        await fetchCsrfToken();
      } catch (e) {
        // CSRF token 获取失败（可能是离线或网络错误），设置为非 admin
        setIsAdmin(false);
        setToken('');
        try {
          window.dispatchEvent(new CustomEvent('app:admin-status-changed', { 
            detail: { isAdmin: false } 
          }));
        } catch (_) {}
        return;
      }
      
      // 刷新页面时：即使没有本地 hint，也应该探测一次 session（服务端对未登录返回 200 success:false，不会产生 401 噪音）
      const wasLoggedIn = await validateSession();
      
      // 如果检测到是 admin 用户，注销 Service Worker
      if (wasLoggedIn) {
        try {
          const result = await unregisterServiceWorker();
          // 只在第一次执行时打印一次日志，避免重复（React Strict Mode 会执行两次）
          if (!swUnregisterLoggedRef.current) {
            if (result) {
              console.log('[Admin] Service Worker unregistered for admin user');
            }
            swUnregisterLoggedRef.current = true;
          }
        } catch (e) {
          if (!swUnregisterLoggedRef.current) {
            console.warn('[Admin] Failed to unregister Service Worker:', e);
            swUnregisterLoggedRef.current = true;
          }
        }
        
        // 如果 Service Worker 仍然存在，通知它进入 admin 模式
        try {
          if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({
              type: 'SET_ADMIN_MODE',
              enabled: true
            });
          } else {
            navigator.serviceWorker.ready.then((registration) => {
              if (registration.active) {
                registration.active.postMessage({
                  type: 'SET_ADMIN_MODE',
                  enabled: true
                });
              }
            }).catch(() => {});
          }
        } catch (e) {
          console.warn('[Admin] Failed to notify Service Worker:', e);
        }
      } else {
        // 非 admin：确保注册 Service Worker（若用户在“离线音乐”开关关闭则会被 musicCache 层拦截）
        try {
          const registration = await registerServiceWorker(false);
          if (registration) {
            console.log('[Admin] Service Worker registered for non-admin user');
          }
          // 如果返回 null，说明被 musicCache 层拦截（离线音乐已禁用等），不打印日志
        } catch (e) {
          console.warn('[Admin] Failed to register Service Worker:', e);
        }
      }
      // 初始化完成后通知 header.js 当前登录状态
      try {
        window.dispatchEvent(new CustomEvent('app:admin-status-changed', { 
          detail: { isAdmin: wasLoggedIn, isLoggedIn: wasLoggedIn } 
        }));
      } catch (_) {}
    };
    init();
    
    // 监听在线/离线状态变化，离线时确保不显示管理按钮
    const handleOnline = () => {
      // 在线时：刷新 admin 登录态（无论是否有本地 hint）
      validateSession().then((loggedIn) => {
          // 如果检测到是 admin 用户，注销 Service Worker
          if (loggedIn) {
            unregisterServiceWorker().catch(e => {
              console.warn('[Admin] Failed to unregister Service Worker:', e);
            });
            
            // 通知 Service Worker 进入 admin 模式
            try {
              if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
                navigator.serviceWorker.controller.postMessage({
                  type: 'SET_ADMIN_MODE',
                  enabled: true
                });
              } else {
                navigator.serviceWorker.ready.then((registration) => {
                  if (registration.active) {
                    registration.active.postMessage({
                      type: 'SET_ADMIN_MODE',
                      enabled: true
                    });
                  }
                }).catch(() => {});
              }
            } catch (e) {
              console.warn('[Admin] Failed to notify Service Worker:', e);
            }
          } else {
            // 如果验证失败，说明已登出，注册 Service Worker
            registerServiceWorker(false).catch(e => {
              console.warn('[Admin] Failed to register Service Worker:', e);
            });
          }
          try {
            window.dispatchEvent(new CustomEvent('app:admin-status-changed', { 
              detail: { isAdmin: loggedIn, isLoggedIn: loggedIn } 
            }));
          } catch (_) {}
        });
    };
    const handleOffline = () => {
      // 离线时，确保不显示管理按钮
      setIsAdmin(false);
      try {
        window.dispatchEvent(new CustomEvent('app:admin-status-changed', { 
          // 这里只负责管理端状态；普通用户登录态由 UserContext/App 桥接负责，避免误把“已登录用户”显示为未登录
          detail: { isAdmin: false } 
        }));
      } catch (_) {}
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [fetchCsrfToken, validateSession]);

  // 设置 Axios 拦截器
  React.useEffect(() => {
    try {
      const axios = require('axios').default || require('axios');
      setupAxiosInterceptors(axios);
    } catch (e) {
      // Axios 不可用，使用 fetch
    }
  }, []);

  // 登录
  const login = async (username, password, twoFactorToken = null) => {
    try {
      let endpoint = '/api/auth/login';
      let body = { username, password };
      
      // 如果提供了2FA token，使用2FA验证端点
      if (twoFactorToken) {
        endpoint = '/api/auth/verify-2fa';
        body = { username, password, token: twoFactorToken };
      }
      
      // 简化的登录请求，不再需要 CSRF token
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include', // 包含 Cookie
        body: JSON.stringify(body)
      });
      
      if (!res.ok) {
        // 兼容：服务端可能返回非 JSON（如 500 的默认 HTML），这里尽量提取可读原因
        const rawText = await res.text().catch(() => '');
        let error = null;
        try {
          error = rawText ? JSON.parse(rawText) : null;
        } catch (_) {
          error = null;
        }
        const msgFromServer =
          (error && (error.message || error.error)) ||
          (rawText && rawText.trim() ? rawText.trim().slice(0, 200) : '') ||
          `HTTP ${res.status}`;

        const err = new Error(msgFromServer || '登录失败');
        err.httpStatus = res.status;
        err.errorData = error || { error: msgFromServer, status: res.status }; // 传递错误数据（包括锁定信息和剩余尝试次数）
        throw err;
      }

      const data = await res.json();
      
      // 检查是否需要2FA验证
      if (data.requires2FA) {
        const err = new Error(data.message || '请输入两步验证码');
        err.requires2FA = true;
        throw err;
      }
      
      // 如果返回了 Access Token，保存它（用于向后兼容）
      if (data.accessToken) {
        setToken(data.accessToken);
      }
      
      setIsAdmin(true);
      setAdminSessionHint(true);
      
      // admin 用户登录后，注销 Service Worker，禁用所有离线功能
      try {
        await unregisterServiceWorker();
        console.log('[Admin] Service Worker unregistered for admin user');
      } catch (e) {
        console.warn('[Admin] Failed to unregister Service Worker:', e);
      }
      
      // 如果 Service Worker 仍然存在（可能正在运行），通知它进入 admin 模式
      // 这确保即使 Service Worker 没有被完全注销，所有请求也会直接穿透
      try {
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({
            type: 'SET_ADMIN_MODE',
            enabled: true
          });
          console.log('[Admin] Notified Service Worker to enable admin mode');
        } else {
          // 如果没有 controller，等待 ready 后再发送
          navigator.serviceWorker.ready.then((registration) => {
            if (registration.active) {
              registration.active.postMessage({
                type: 'SET_ADMIN_MODE',
                enabled: true
              });
              console.log('[Admin] Notified Service Worker to enable admin mode (after ready)');
            }
          }).catch(() => {});
        }
      } catch (e) {
        console.warn('[Admin] Failed to notify Service Worker:', e);
      }
      
      // 登录成功后通知 header.js 更新按钮
      try {
        window.dispatchEvent(new CustomEvent('app:admin-status-changed', { 
          detail: { isAdmin: true } 
        }));
      } catch (_) {}
      return true;
    } catch (e) {
      console.error('Login error:', e);
      throw e;
    }
  };

  // 登出
  const logout = async () => {
    try {
      const config = await getSecureRequestConfig({
        method: 'POST'
      });
      
      await secureFetch('/api/auth/logout', config).catch(() => {
        // 即使登出 API 失败，也清除本地状态
      });
    } catch (e) {
      console.error('Logout error:', e);
    } finally {
      setIsAdmin(false);
      setToken('');
      clearAuthStorage();
      setAdminSessionHint(false);
      
      // admin 用户登出后，重新注册 Service Worker，恢复离线功能
      try {
        const registration = await registerServiceWorker(false);
        if (registration) {
          console.log('[Admin] Service Worker re-registered after logout');
        }
        // 如果返回 null，说明被 musicCache 层拦截（离线音乐已禁用等），不打印日志
      } catch (e) {
        console.warn('[Admin] Failed to re-register Service Worker:', e);
      }
      
      // 通知 Service Worker 退出 admin 模式
      try {
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({
            type: 'SET_ADMIN_MODE',
            enabled: false
          });
          console.log('[Admin] Notified Service Worker to disable admin mode');
        } else {
          navigator.serviceWorker.ready.then((registration) => {
            if (registration.active) {
              registration.active.postMessage({
                type: 'SET_ADMIN_MODE',
                enabled: false
              });
            }
          }).catch(() => {});
        }
      } catch (e) {
        console.warn('[Admin] Failed to notify Service Worker:', e);
      }
      
      // 登出后通知 header.js 更新按钮
      try {
        window.dispatchEvent(new CustomEvent('app:admin-status-changed', { 
          detail: { isAdmin: false } 
        }));
      } catch (_) {}
    }
  };

  // 获取认证 Token（向后兼容）
  const getAuthToken = () => {
    return token;
  };

  return (
    <AdminContext.Provider value={{ isAdmin, token, login, logout, getAuthToken }}>
      {children}
    </AdminContext.Provider>
  );
}


